import { GLOBAL_ASSETS_SENTINEL_OWNER_ID } from '@cadstrom/cadstrom-shared';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useTRPC } from '#src/trpc.js';
import { getOrganizationDisplayName, sortOrganizations } from './sort-organizations.js';

import type { Organization } from '@cadstrom/cadstrom-shared';

export const GLOBAL_LABEL = 'Global (shared with everyone)';

const EMPTY_OPTION: Organization = {
	id: '',
	organizationName: '',
	displayName: null,
	createdAt: new Date(0),
};

export type OwnerOrganizationPickerProps = {
	value: string | undefined;
	onChange: (organizationId: string | undefined) => void;
	globalOptionPosition: 'first' | 'last';
	disabled?: boolean;
};

/**
 * Dropdown picker for selecting an owner organization. The "Global" sentinel is rendered with
 * a distinct label and can be pinned to the first or last position based on audience.
 *
 * @param props - Component props.
 * @param props.value - Selected organization id, or undefined when nothing is selected.
 * @param props.onChange - Called with the selected organization id on change.
 * @param props.globalOptionPosition - Where to place the Global sentinel in the dropdown.
 * @param props.disabled - Whether the picker is disabled.
 */
export function OwnerOrganizationPicker({
	value,
	onChange,
	globalOptionPosition,
	disabled,
}: OwnerOrganizationPickerProps) {
	const trpc = useTRPC();

	const { data: organizationsData } = useQuery(trpc.organization.getMany.queryOptions({}));
	const organizations = organizationsData?.organizations;

	const sortedOptions = useMemo(
		() => (organizations === undefined ? [] : sortOrganizations(organizations, globalOptionPosition)),
		[organizations, globalOptionPosition],
	);

	const selectedOption = organizations?.find((org) => org.id === value) ?? EMPTY_OPTION;

	return (
		<Autocomplete
			loading={organizations === undefined}
			disabled={disabled}
			autoHighlight
			disableClearable
			handleHomeEndKeys
			openOnFocus
			options={sortedOptions.length > 0 ? sortedOptions : [EMPTY_OPTION]}
			value={selectedOption}
			onChange={(_event, newValue) => {
				if (newValue.id === '') {
					return;
				}
				onChange(newValue.id);
			}}
			isOptionEqualToValue={(option, candidate) => option.id === candidate.id}
			getOptionLabel={(option) =>
				option.id === GLOBAL_ASSETS_SENTINEL_OWNER_ID ? GLOBAL_LABEL : getOrganizationDisplayName(option)
			}
			renderInput={(params) => <TextField {...params} label='Owner Organization' />}
			renderOption={({ key: _key, ...props }, option) => (
				<li key={option.id} {...props}>
					<span>
						{option.id === GLOBAL_ASSETS_SENTINEL_OWNER_ID ? GLOBAL_LABEL : getOrganizationDisplayName(option)}
					</span>
				</li>
			)}
		/>
	);
}
